/**
 * BootRevalidator - Targeted background revalidation on app boot
 *
 * Spotify-model boot behavior:
 * - Render instantly from persisted cache (no loading states)
 * - Background revalidate ONLY what matters (no global refetch storm)
 * - Current page always revalidates so it never stays stale for 30-60s
 *
 * Triggers ONCE per hydrated userId after:
 * - Auth is initialized
 * - QueryCachePersister hydration is complete for this user
 *
 * Two kinds of revalidation:
 * A) Core allowlist (always): lists.mine, notifications, watchlist.followed
 * B) Current route (always): movie/tv/person/list/profile detail queries
 */

import { useEffect, useRef } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useCacheHydration } from './QueryCachePersister';
import { queryKeys } from '../lib/queryKeys';

const BOOT_REVALIDATION_DELAY_MS = 250; // Debounce to avoid competing with initial render

export function BootRevalidator() {
  const { user, initialized, needsUsername } = useAuth();
  const { isHydrated, hydratedUserId } = useCacheHydration();
  const queryClient = useQueryClient();
  const location = useLocation();

  // Track which userId we've already run boot revalidation for
  const didRunForUserIdRef = useRef<string | null>(null);

  // Store location in ref so timeout can read current value at execution time
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    // Gate: must be initialized + hydrated + user matches hydrated user
    if (!initialized) return;
    if (!isHydrated) return;
    if (!user?.id) {
      // Logged out - reset ref so next login can revalidate
      didRunForUserIdRef.current = null;
      return;
    }

    // Skip boot revalidation while user still needs to claim username.
    // Do NOT reset didRunForUserIdRef — when needsUsername transitions to false
    // after successful claim, the effect will re-run and revalidate normally.
    if (needsUsername) return;

    // Only run if hydrated user matches current user
    if (hydratedUserId !== user.id) return;

    // Only run ONCE per hydrated userId
    if (didRunForUserIdRef.current === user.id) return;

    // NOTE: Do NOT set didRunForUserIdRef here - only inside the timeout callback
    // If we set it here and the effect re-runs (route change), the timeout is cancelled
    // but the ref is already set, preventing rescheduling.

    const userId = user.id;

    // Debounce to avoid competing with initial render
    const timeout = setTimeout(() => {
      // Mark as run for this user INSIDE the callback (only when actually executing)
      didRunForUserIdRef.current = userId;

      // Read pathname at execution time (not when effect was scheduled)
      const pathname = locationRef.current.pathname;

      console.log('[BootRevalidator] Running boot revalidation for user:', userId);

      // ========================================================================
      // A) Core allowlist - always revalidate these
      // ========================================================================
      const coreQueries = [
        // User's lists
        queryKeys.lists.mine(userId),
        // Notifications unread count (badge)
        queryKeys.notifications.unreadCount(userId),
        // Notifications dropdown first page only
        queryKeys.notifications.list(userId, { limit: 10, cursor: null, type: null }),
        // Followed watchlists
        queryKeys.watchlist.followed(userId),
      ];

      // Invalidate core queries (refetchType: 'active' = only mounted/observed)
      for (const queryKey of coreQueries) {
        queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
      }

      // ========================================================================
      // B) Current route revalidation - always revalidate current page's data
      // ========================================================================

      // /movie/:id
      const movieMatch = matchPath('/movie/:id', pathname);
      if (movieMatch?.params.id) {
        const movieId = Number(movieMatch.params.id);
        if (!isNaN(movieId)) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.movie.detail(movieId),
            refetchType: 'active',
          });
        }
      }

      // /tv/:id
      const tvMatch = matchPath('/tv/:id', pathname);
      if (tvMatch?.params.id) {
        const tvId = Number(tvMatch.params.id);
        if (!isNaN(tvId)) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.tv.detail(tvId),
            refetchType: 'active',
          });
        }
      }

      // /person/:id
      const personMatch = matchPath('/person/:id', pathname);
      if (personMatch?.params.id) {
        const personId = Number(personMatch.params.id);
        if (!isNaN(personId)) {
          queryClient.invalidateQueries({
            queryKey: queryKeys.person.detail(personId),
            refetchType: 'active',
          });
        }
      }

      // /list/:id
      const listMatch = matchPath('/list/:id', pathname);
      if (listMatch?.params.id) {
        const listId = listMatch.params.id;
        // Detail: invalidate immediately
        queryClient.invalidateQueries({
          queryKey: queryKeys.lists.detail(listId),
          refetchType: 'active',
        });
        // Items: delay to let outbox replay complete first
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.lists.itemsBase(listId),
            exact: false,
            refetchType: 'active',
          });
        }, 2000);
      }

      // /u/:username
      const profileMatch = matchPath('/u/:username', pathname);
      if (profileMatch?.params.username) {
        const usernameLower = profileMatch.params.username.toLowerCase();
        queryClient.invalidateQueries({
          queryKey: queryKeys.profile.byUsername(usernameLower),
          refetchType: 'active',
        });
      }

      // /u/:username/followers or /u/:username/following
      const followersMatch = matchPath('/u/:username/followers', pathname);
      const followingMatch = matchPath('/u/:username/following', pathname);
      const followPageMatch = followersMatch || followingMatch;
      if (followPageMatch?.params.username) {
        const usernameLower = followPageMatch.params.username.toLowerCase();

        // Invalidate username resolver query (used by FollowersPage)
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.byUsername(usernameLower),
          refetchType: 'active',
        });

        // Resolve profileId from cache: try users.byUsername first, fallback to profile.byUsername
        const cachedFromUsers = queryClient.getQueryData<{ id: string }>(
          queryKeys.users.byUsername(usernameLower)
        );
        const cachedFromProfile = queryClient.getQueryData<{ id: string }>(
          queryKeys.profile.byUsername(usernameLower)
        );
        const cachedProfileId = cachedFromUsers?.id ?? cachedFromProfile?.id;

        if (cachedProfileId) {
          const viewerBucket = userId; // We're logged in at this point
          const followQueryKey = followersMatch
            ? queryKeys.users.followers(cachedProfileId, viewerBucket)
            : queryKeys.users.following(cachedProfileId, viewerBucket);

          // Mark stale without immediate refetch — preserves visible cached rows during initial paint
          queryClient.invalidateQueries({
            queryKey: followQueryKey,
            refetchType: 'none',
          });

          // Deferred background refresh after initial paint stabilizes (no skeleton flash)
          setTimeout(() => {
            queryClient.refetchQueries({
              queryKey: followQueryKey,
              type: 'active',
            });
          }, 500);
        }
      }

      console.log('[BootRevalidator] Boot revalidation complete');
    }, BOOT_REVALIDATION_DELAY_MS);

    return () => clearTimeout(timeout);
    // NOTE: Do NOT include location.pathname - we read it from ref at execution time
    // This prevents the effect from re-running on route changes (which would cancel the timeout)
  }, [initialized, isHydrated, hydratedUserId, user?.id, needsUsername, queryClient]);

  // Render nothing - this is a bootstrap component
  return null;
}
